import type { AnyFieldApi } from "@tanstack/solid-form";
import { type ComponentProps, createMemo, Show, splitProps } from "solid-js";
import { ZodError } from "zod";
import { cn } from "@/libs/cn";
import { hasZodErrorShape } from "@/libs/zod-errors";

export type InputProps = ComponentProps<"input">;

const Input = <T extends InputProps = InputProps>(props: T) => {
  const [className, restProps] = splitProps<T, [readonly (keyof T)[]]>(props, [
    "class",
    "classList",
  ]);
  return (
    <input
      {...restProps}
      class={cn(
        "flex h-10 w-full rounded-md border border-input bg-card p-4 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-[1.5px] focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className.class,
      )}
      classList={className.classList}
    />
  );
};

type FieldInfoProps = {
  field: AnyFieldApi;
  helpText?: string;
} & ComponentProps<"div">;

function FieldInfo(props: FieldInfoProps) {
  const [localProps, restProps] = splitProps<
    FieldInfoProps,
    [readonly (keyof FieldInfoProps)[]]
  >(props, ["field", "class", "classList", "helpText"]);

  const message = createMemo(() => {
    const errors = localProps.field.state.meta.errors;
    if (!errors || errors.length === 0) return null;
    const error = errors[0];
    if (hasZodErrorShape(error) || error instanceof ZodError) {
      return (error as ZodError).message;
    }
    return typeof error === "string" ? error : String(error);
  });

  return (
    <div
      {...restProps}
      class={cn(localProps.class)}
      classList={localProps.classList}
    >
      <Show
        when={!!message()}
        fallback={
          localProps.helpText ? (
            <p class="text-xs text-gray-500">{localProps.helpText}</p>
          ) : null
        }
      >
        <p class="text-red-500 text-xs">{message()}</p>
      </Show>
      <Show when={localProps.field.state.meta.isValidating}>
        <p class="text-xs text-gray-500">Validating...</p>
      </Show>
    </div>
  );
}

const FieldContainer = (props: ComponentProps<"div">) => {
  return (
    <div
      {...props}
      class={cn("flex flex-col gap-1 w-full", props.class)}
      classList={props.classList}
    />
  );
};

const FieldGroup = (props: ComponentProps<"div">) => {
  const [localProps, restProps] = splitProps<
    ComponentProps<"div">,
    [readonly (keyof ComponentProps<"div">)[]]
  >(props, ["class", "classList", "children"]);
  return (
    <div
      {...restProps}
      class={cn(
        "flex flex-col gap-4 w-full border-b border-b-gray-200 pb-4",
        localProps.class,
      )}
      classList={localProps.classList}
    >
      {localProps.children}
    </div>
  );
};

const FieldSectionTitle = (props: ComponentProps<"p">) => {
  const [localProps, restProps] = splitProps<
    ComponentProps<"p">,
    [readonly (keyof ComponentProps<"p">)[]]
  >(props, ["class", "classList"]);
  return (
    <p
      class={cn("text-md font-medium text-neutral-500", localProps.class)}
      {...restProps}
    />
  );
};

const FieldContainerLabel = (props: ComponentProps<"label">) => {
  const [localProps, restProps] = splitProps<
    ComponentProps<"label">,
    [readonly (keyof ComponentProps<"label">)[]]
  >(props, ["class", "classList", "for"]);
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: label is associated with an input
    <label
      class={cn("text-sm font-medium", localProps.class)}
      {...restProps}
      for={props.for}
    />
  );
};

const FieldContainerDescriptionHelpText = (props: ComponentProps<"p">) => {
  const [localProps, restProps] = splitProps<
    ComponentProps<"p">,
    [readonly (keyof ComponentProps<"p">)[]]
  >(props, ["class", "classList"]);
  return (
    <p class={cn("text-xs text-gray-500", localProps.class)} {...restProps} />
  );
};

export {
  FieldContainer,
  FieldContainerDescriptionHelpText,
  FieldContainerLabel,
  FieldGroup,
  FieldInfo,
  FieldSectionTitle,
  Input,
};
